import { graphql } from '@data-access/graphql'
import type { ResultOf, VariablesOf } from '@graphql-typed-document-node/core'

const NexperienceSearchItemFragmentDoc = graphql(`
  fragment NexperienceSearchItem on NexperienceSearchItem {
    __typename
    id
    publicId
    duration
    hasTimeslots
    hasMultipleDurations
    hasMultiplePrices
    images {
      url
      alt
    }
    location
    price
    priceCategory
    currency
    slug
    title
    lat
    lng
    primaryActivity
  }
`)

const NexperienceTimeslotFragmentDoc = graphql(`
  fragment NexperienceTimeslot on NexperienceTimeslot {
    id
    available
    startDate {
      local
      timezone
      utc
    }
    endDate {
      local
      timezone
      utc
    }
    bookableUntil {
      local
      timezone
      utc
    }
    availableByOption {
      optionId
      min
      max
    }
  }
`)

const NexperienceDatesWithTimeslotsFragmentDoc = graphql(`
  fragment NexperienceDatesWithTimeslots on NexperienceAvailableDate {
    date
    timeslots {
      ...NexperienceTimeslot
    }
  }
`)

const NexperienceFragmentDoc = graphql(`
  fragment Nexperience on Nexperience {
    __typename
    id
    phoneNumberNeeded
    publicId
    externalId
    slug
    title
    duration
    experienceType
    alwaysAvailable
    hasMultipleDurations
    hasMultiplePrices
    descriptionHtml
    additionalDescriptionHtml
    userTimeslotRequestsAllowed
    missionTags
    inclusive
    requiredItems
    minAge
    cancelationDeadline
    registrationDeadline
    importantNotesHtml
    activities
    suitableFor
    languages
    maxParticipants
    requirements
    metaTitle
    metaDescription
    offerPeriodStart {
      local
      timezone
      utc
    }
    offerPeriodEnd {
      local
      timezone
      utc
    }
    destination {
      name
      slug
    }
    location {
      lat
      lng
      address
      city
      name
      description
      varying
    }
    publicTransport
    parkingOptions
    images {
      url
      alt
      copyright
      thumbnailUrl
    }
    merchant {
      associationRegistrationNumber
      city
      companyRegistrationNumber
      createdAt
      profileText
      tradeName
      legalForm
      legalName
      country
      street
      vatId
      zipCode
      image {
        url
        alt
      }
      publicId
      slug
      profileActive
      memberSince
    }
    highlights
    basePrice
    currency
    priceCategory
    variants {
      ...NexperienceVariant
      options {
        priceId
      }
    }
  }
`)

export const NexperienceSearchDocument = graphql(`
  query nexperienceSearch(
    $filter: ExperienceSearchInput!
    $locale: String!
    $pagination: PaginationInput
    $sorting: ProductSorting
  ) {
    nexperienceSearch(
      filter: $filter
      locale: $locale
      pagination: $pagination
      sorting: $sorting
    ) {
      count
      data {
        ...NexperienceSearchItem
      }
    }
  }
`)

export const NexperienceSuggestionsDocument = graphql(`
  query nexperienceSuggestions($publicId: Float!, $filter: NexperienceSuggestionsFilter) {
    nexperienceSuggestions(publicId: $publicId, filter: $filter) {
      hasMore
      experiences {
        ...NexperienceSearchItem
      }
    }
  }
`)

export const NexperienceAvailableDatesDocument = graphql(`
  query nexperienceAvailableDates(
    $publicId: Int!
    $variantId: String!
    $filter: NexperienceAvailableDatesFilterInput
    $previewToken: String
  ) {
    nexperienceAvailableDates(
      publicId: $publicId
      variantId: $variantId
      filter: $filter
      previewToken: $previewToken
    ) {
      count
      dates {
        date
      }
    }
  }
`)

export const NexperienceAvailableAndAlternativeDatesDocument = graphql(`
  query nexperienceAvailableAndAlternativeDates(
    $publicId: Int!
    $variantId: String!
    $filter: NexperienceAvailableDatesFilterInput
    $pagination: PaginationInput
    $previewToken: String
  ) {
    nexperienceAvailableDates(
      publicId: $publicId
      variantId: $variantId
      filter: $filter
      pagination: $pagination
      previewToken: $previewToken
    ) {
      count
      dates {
        ...NexperienceDatesWithTimeslots
      }
      alternativeDates {
        ...NexperienceDatesWithTimeslots
      }
    }
  }
`)

export const NexperienceTimeslotsDocument = graphql(`
  query nexperienceTimeslots(
    $publicId: Int!
    $variantId: String!
    $filter: NexperienceAvailableDatesFilterInput
    $pagination: PaginationInput
    $previewToken: String
  ) {
    nexperienceAvailableDates(
      publicId: $publicId
      variantId: $variantId
      filter: $filter
      pagination: $pagination
      previewToken: $previewToken
    ) {
      count
      dates {
        date
        timeslots(filter: $filter) {
          ...NexperienceTimeslot
        }
      }
    }
  }
`)

export const NexperienceRecommendationsDocument = graphql(`
  query nexperienceRecommendations($input: GetRecommendationsInput!) {
    getRecommendations(input: $input) {
      nexperiences {
        __typename
        id
        publicId
        duration
        hasTimeslots
        hasMultipleDurations
        hasMultiplePrices
        images {
          url
          alt
        }
        location
        price
        priceCategory
        currency
        slug
        title
        lat
        lng
        primaryActivity
      }
    }
  }
`)

export const NexperienceDocument = graphql(`
  query nexperience(
    $locale: String!
    $previewToken: String
    $publicId: Int!
    $options: NexperienceOptionsInput
  ) {
    nexperience(
      locale: $locale
      previewToken: $previewToken
      publicId: $publicId
      options: $options
    ) {
      ...Nexperience
    }
  }
`)

export const ValidateTimeslotDocument = graphql(`
  query validateTimeslot($input: TimeslotValidationInput!) {
    validateTimeslot(input: $input)
  }
`)

export type NexperienceSearchQuery = ResultOf<typeof NexperienceSearchDocument>
export type NexperienceSearchQueryVariables = VariablesOf<typeof NexperienceSearchDocument>
export type NexperienceAvailableDatesQueryVariables = VariablesOf<
  typeof NexperienceAvailableDatesDocument
>
export type NexperienceAvailableAndAlternativeDatesQueryVariables = VariablesOf<
  typeof NexperienceAvailableAndAlternativeDatesDocument
>
export type NexperienceTimeslotsQuery = ResultOf<typeof NexperienceTimeslotsDocument>
export type NexperienceTimeslotsQueryVariables = VariablesOf<typeof NexperienceTimeslotsDocument>
export type NexperienceRecommendationsQuery = ResultOf<typeof NexperienceRecommendationsDocument>
export type NexperienceQuery = ResultOf<typeof NexperienceDocument>
export type NexperienceQueryVariables = VariablesOf<typeof NexperienceDocument>

export type NexperienceSearchItemFragment = ResultOf<typeof NexperienceSearchItemFragmentDoc>
export type NexperienceTimeslotFragment = ResultOf<typeof NexperienceTimeslotFragmentDoc>
export type NexperienceDatesWithTimeslotsFragment = ResultOf<
  typeof NexperienceDatesWithTimeslotsFragmentDoc
>
export type NexperienceFragment = ResultOf<typeof NexperienceFragmentDoc>

export const NexperienceOpeningHoursDocument = graphql(`
  query nexperienceOpeningHours(
    $locale: String!
    $publicId: Int!
    $from: String!
    $to: String!
    $previewToken: String
  ) {
    nexperience(locale: $locale, publicId: $publicId, previewToken: $previewToken) {
      id
      experienceType
      alwaysAvailable
      openingHours(from: $from, to: $to) {
        date
        weekday
        closed
        windows {
          from
          to
        }
      }
    }
  }
`)

export const NexperienceDayPricesDocument = graphql(`
  query nexperienceDayPrices($experienceId: String!, $from: String!, $to: String!) {
    nexperienceDayPrices(experienceId: $experienceId, from: $from, to: $to) {
      date
      basePrice
      priceCategory
      prices {
        priceId
        price
      }
    }
  }
`)

export type NexperienceDayPricesQuery = ResultOf<typeof NexperienceDayPricesDocument>
